import type { UnknownAction } from 'redux';

import { STREAM_EVENT_MESSAGE_METADATA } from '../../actions/aiAssistant.actionTypes';
import { ADD_COLLECTION } from '../../actions/collections.actionTypes';
import { STORE_DOWNLOADED_ITEM } from '../../actions/download.actionTypes';
import { routes } from '../../constants';

export type ReportCardAreaId = 'download' | 'aiRequest' | 'collection';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

export interface ReportCardArea {
	id: ReportCardAreaId;
	/** Server-backed onboarding-hint key that persists this area's completion (WOR-795). */
	hintKey: string;
	/** Redux action type whose dispatch marks this area complete. */
	completionActionType: string;
	/**
	 * Optional extra guard when a type match alone isn't enough. Keeps per-area
	 * matching declarative here rather than special-cased in the middleware.
	 */
	matches?: (action: UnknownAction) => boolean;
	/** Route the dialog CTA links to while the area is still incomplete. */
	ctaRoute: string;
	/** i18n key for the area's row label (copy wired up in Phase 2). */
	labelKey: string;
}

/**
 * Single source of truth for the Trial Report Card's three graded areas. The
 * recording middleware, the progress hook, and the (Phase 2) UI all derive
 * from this list, so the areas can't drift between recording and rendering.
 *
 * The AI area keys off `STREAM_EVENT_MESSAGE_METADATA` (which carries a message
 * `role`) rather than the role-less `STREAM_EVENT_METADATA`. The producer
 * dispatches this action only for user-authored messages today, but the
 * underlying SDK stream also emits message-metadata for the assistant reply;
 * its `matches` predicate guards on `payload.metadata.role === 'user'` so the
 * AI area stays tied to genuine user requests regardless.
 */
export const reportCardAreas: ReportCardArea[] = [
	{
		id: 'download',
		hintKey: 'report-card-download',
		completionActionType: STORE_DOWNLOADED_ITEM,
		ctaRoute: `/${routes.search.root}`,
		labelKey: 'Download material',
	},
	{
		id: 'aiRequest',
		hintKey: 'report-card-ai-request',
		completionActionType: STREAM_EVENT_MESSAGE_METADATA,
		// Only called once `type` matched, but the payload is still validated: an
		// action with missing or malformed metadata must not throw inside the
		// middleware, and it cannot count as a user request.
		matches: ({ payload }) =>
			isRecord(payload) && isRecord(payload.metadata) && payload.metadata.role === 'user',
		ctaRoute: `/${routes.aiTools.root}`,
		labelKey: 'Create with AI',
	},
	{
		id: 'collection',
		hintKey: 'report-card-collection',
		completionActionType: ADD_COLLECTION.SUCCESS,
		ctaRoute: `/${routes.collections.root}`,
		labelKey: 'Create a collection',
	},
];

export const TOTAL_REPORT_CARD_AREAS = reportCardAreas.length;
